import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { AuthenticationService, BaseComponent } from '@app/core';
import { Country, CourseCustomer } from '@app/models';
import { CourseUserProfileService } from '@app/pages/course-user-profile/course-user-profile.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import * as moment from 'moment';
import { debounceTime, filter, forkJoin } from 'rxjs';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { TranslateService } from '@ngx-translate/core';
import { AlertDialogComponent } from '../alert-dialog/alert-dialog.component';

export const DATE_FORMAT = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'DD.MM.YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@UntilDestroy()
@Component({
  selector: 'profile-confirmation-dialog',
  templateUrl: 'profile-confirmation-dialog.component.html',
  styleUrls: ['profile-confirmation-dialog.component.scss'],
  providers: [CourseUserProfileService, provideMomentDateAdapter(DATE_FORMAT)],
})
export class ProfileConfirmationDialogComponent
  extends BaseComponent
  implements OnInit
{
  public form!: FormGroup;

  public isInit: boolean = false;

  public countries: Country[] = [];

  public maxDate: Date;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public message: any;

  constructor(
    public dialogRef: MatDialogRef<ProfileConfirmationDialogComponent>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _fb: FormBuilder,
    private courseUserProfileService: CourseUserProfileService,
    private authenticationService: AuthenticationService,
    private dialog: MatDialog,
    private translateService: TranslateService,
  ) {
    super();
    this.dialogRef.disableClose = true;

    const today = new Date();
    this.maxDate = new Date(
      today.getFullYear() - 5,
      today.getMonth(),
      today.getDate(),
    );
  }

  ngOnInit(): void {
    if (this.data && this.data.message) {
      this.message = this.data.message;
    }

    const combinedSubscriptions = forkJoin({
      countries: this.courseUserProfileService.getCountries(),
      meData: this.courseUserProfileService.getMeData(),
    });

    // Abonniere die kombinierten Ergebnisse
    this.addSubscription(combinedSubscriptions)
      .pipe(filter((resp) => !!resp && !!resp.meData && !!resp.meData.data))
      .subscribe({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        next: (resp: any) => {
          // Hier kannst du auf beide Ergebnisse zugreifen
          this.countries = resp.countries.data;
          this.initForm(resp.meData.data);
          this.isInit = true;
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        error: () => {
          this.dialogRef.close();
        },
      });
  }

  initForm(data: CourseCustomer) {
    this.form = this._fb.group({
      id: '',
      first_name: ['', Validators.required],
      last_name: ['', Validators.required],
      full_name: '',
      email: [
        '',
        [
          Validators.required,
          Validators.email,
          (control: AbstractControl) => {
            const form = control.parent as FormGroup;
            if (!form) return null;
            return form.get('email_confirmation')?.value === control.value
              ? null
              : { emailMismatch: true };
          },
        ],
      ],
      email_confirmation: [
        '',
        [
          Validators.required,
          Validators.email,
          (control: AbstractControl) => {
            const form = control.parent as FormGroup;
            if (!form) return null;
            return form.get('email')?.value === control.value
              ? null
              : { emailMismatch: true };
          },
        ],
      ],
      phone_number: '',
      birthplace: '',
      birthday: ['', Validators.required],
      street: ['', Validators.required],
      housenumber: ['', Validators.required],
      zip_code: ['', Validators.required],
      city: ['', Validators.required],
      country: [this._fb.control({ id: '' }), Validators.required],
    });

    const emailConfirmation = this.form.get('email_confirmation');
    const email = this.form.get('email');

    if (emailConfirmation && email) {
      emailConfirmation.valueChanges.pipe(debounceTime(300)).subscribe(() => {
        email.updateValueAndValidity();
      });

      email.valueChanges.pipe(debounceTime(300)).subscribe(() => {
        emailConfirmation.updateValueAndValidity();
      });
    }

    if (data) {
      this.form.patchValue(data);
      this.form
        .get('country')
        ?.patchValue(this.countries.find((c) => c.id === data.country?.id));
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  close(data?: any): void {
    this.dialogRef.close(data);
  }

  confirm(event: MouseEvent) {
    this.form.markAllAsTouched();

    this.form.markAsDirty();
    this.form.markAsTouched();
    this.form.updateValueAndValidity();
    if (!this.form.invalid) {
      this.addSubscription(this.openUserInputConfirmationDialog()).subscribe(
        (resp) => {
          if (resp && resp.action?.index === 'confirm') {
            const formValue = this.form.value;
            delete formValue.email_confirmation;
            this.addSubscription(
              this.courseUserProfileService.saveMeData(formValue),
            ).subscribe(
              (resp) => {
                if (resp) {
                  this.close({ event: event, action: 'confirm' });
                }
              },
              (error) => {
                console.error(error);
              },
            );
          }
        },
      );
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dateFormat(e: any) {
    let value = e.target.value;
    value = moment(value, 'DD.MM.YYYY', true).toDate();

    let dateValue = value;
    if (!moment.isMoment(dateValue)) {
      dateValue = moment(dateValue);
    }
    dateValue = dateValue.startOf('day');

    let formatedValue = dateValue;
    const dateFormat = 'YYYY-MM-DDTHH:mm:ss.sss[Z]';
    if (dateFormat) {
      formatedValue = dateValue.format(dateFormat);
    }
    this.form.get('birthday')?.setValue(formatedValue, { emitEvent: false });
  }

  openUserInputConfirmationDialog() {
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      data: {
        title: this.translateService.instant(
          'user_input_confirmation_dialog.title',
        ),
        body: this.translateService.instant(
          'user_input_confirmation_dialog.body',
        ),
        actions: [
          {
            index: 'cancel',
            label: 'user_input_confirmation_dialog.cancel',
            color: 'primary',
          },
          {
            index: 'confirm',
            label: 'user_input_confirmation_dialog.confirm',
            color: 'primary',
          },
        ],
      },
    });

    return this.addSubscription(dialogRef.afterClosed());
  }
}
